<template>
  <ul class="visit-checklist">
    <VisitStepRow
      :class="{
        'visit-checklist__visit-step': true,
        'visit-checklist__visit-step--hidden': visitStep.text.toLowerCase() === 'upcoming actions',
      }"
      v-for="(visitStep, index) in checklistRows"
      :key="visitStep.text"
      :allSteps="checklistRows"
      :index="index"
      :isExpandable="visitStep.isCompedActivitySection || visitStep.isLodgingSection || visitStep.isVisitPlanSection"
      :isExpanded="
        (visitStep.isLodgingSection && !props.lodgingDetailsCollapsed) ||
        (visitStep.isCompedActivitySection && !props.compDetailsCollapsed) ||
        (visitStep.isVisitPlanSection && !props.isVisitPlanCollapsed)
      "
      @toggle="
        () => {
          if (visitStep.isCompedActivitySection) emit('update:compDetailsCollapsed', !props.compDetailsCollapsed);
          if (visitStep.isLodgingSection) emit('update:lodgingDetailsCollapsed', !props.lodgingDetailsCollapsed);
          if (visitStep.isVisitPlanSection) emit('update:isVisitPlanCollapsed', !props.isVisitPlanCollapsed);
        }
      "
    >
      <!-- Lodging Section -->
      <div
        v-if="visitStep.isLodgingSection"
        :style="{
          opacity: props.isSavingCollabInput ? '0.5' : 1,
          pointerEvents: props.isSavingCollabInput ? 'none' : 'auto',
        }"
        class="form-container"
      >
        <!--Mimic the logic in CreateCollab (to decide if they see the lodging step)-->
        <template v-if="collabRemainingCounts && collabRemainingCounts?.showLodgingStep !== false">
          <div style="margin-bottom: 10px">Lodging details here.</div>
          <!-- Note: Similar in CreateCollab.vue -->
          <div class="global-select-wrap" style="margin-bottom: 10px">
            <!--NOTE! Any changes here update StepLodging.vue also!-->
            <select
              class="global-select timeRequired"
              ref="hotelCovered"
              v-model="props.collabInput.hotelNightsCovered"
              @change="
                () => {
                  nightsCoveredChanged();
                  requestCollabInputSave();
                }
              "
            >
              <!-- Note: These need to be numbers, and not strings -->
              <option v-if="collabRemainingCounts.mustCoverLodging === false" :value="0">{{ customerNotCoveringLodgingText }}</option>
              <!-- Show the 1 night option if it's been selected. This is temporary and will be removed when in-flight collabs using 1-night options are completed. -->
              <option v-if="props.collabInput.hotelNightsCovered === 1" :value="1">Can arrange a 1-night stay</option>
              <option :value="2">Can arrange a 2-night stay</option>
            </select>

            <div class="global-select-wrap__arrow">&nbsp;</div>
          </div>

          <template v-if="props.collabInput.hotelNightsCovered > 0">
            <div class="ui input" style="width: 100%; margin-bottom: 10px">
              <input type="text" v-model="props.collabInput.hotelName" placeholder="Lodging name" @change="requestCollabInputSave" />
            </div>

            <div class="ui input" style="width: 100%; margin-bottom: 10px">
              <input type="text" v-model="props.collabInput.hotelDateBooked" placeholder="Lodging dates" @change="requestCollabInputSave" />
            </div>

            <!-- We let them save the above field but this doesn't make sense to set until a creator has been chosen -->
            <div v-if="props.creatorInput && !props.collabInput.hotelIsBooked" class="lodging-checkbox-n-info">
              <SrpButton
                class="lodging-checkbox-n-info__checkbox-component"
                :size="screenSize === 'tablet' ? 'tiny' : 'small'"
                color="orange"
                :isDisabled="props.collabInput.hotelIsBooked"
                @click="
                  () => {
                    props.collabInput.hotelIsBooked = true;
                    requestCollabInputSave();
                  }
                "
                :style="{ maxWidth: screenSize === 'tablet' ? '185px' : 'unset' }"
              >
                Mark Lodging Coordinated
              </SrpButton>

              <InfoIconWithTooltip class="lodging-checkbox-n-info__tooltip-wrap">Check this when the lodging has been fully arranged for the creator.</InfoIconWithTooltip>
            </div>

            <div v-else-if="props.collabInput.hotelIsBooked" style="display: flex; align-items: center">
              <div class="lodging-checkbox-n-info" style="margin-right: 10px">
                <IconEmbedded name="check_4-5" :size="14" style="margin-right: 4px" color="rgba(61, 133, 40, 1)" />
                Lodging is coordinated
              </div>

              <LinkWithIcon
                isDottedUnderline
                style="position: relative; top: -1px"
                @click="
                  () => {
                    props.collabInput.hotelIsBooked = false;
                    requestCollabInputSave();
                  }
                "
              >
                <span>undo</span>
              </LinkWithIcon>
            </div>
          </template>
        </template>

        <!-- Creator Hotel covered -->
        <div v-if="creatorCoveringHotel" class="field">
          <div><b>Creator's Lodging Details</b></div>
          <div v-if="props.creatorInput?.nightsRequired === 0">None required</div>
          <div v-else-if="props.creatorInput">
            <div v-if="props.creatorInput.hotelName?.length > 0">{{ props.creatorInput.hotelName }}</div>
            <div v-if="props.creatorInput.hotelDateBooked?.length > 0">{{ props.creatorInput.hotelDateBooked }}</div>
            <div v-if="!props.creatorInput.hotelName && !props.creatorInput.hotelDateBooked">Creator hasn't filled out lodging details.</div>
          </div>
        </div>
      </div>
      <!-- / Lodging Section -->

      <!-- Comped Activities Section -->
      <div
        v-else-if="visitStep.isCompedActivitySection"
        :style="{
          opacity: props.isSavingCollabInput ? '0.5' : 1,
          pointerEvents: props.isSavingCollabInput ? 'none' : 'auto',
        }"
        class="form-container"
      >
        <div>Any activities you would like to assist with:</div>
        <CollabCompTags :collabInput="props.collabInput" @change="requestCollabInputSave" />

        <!-- Only show the below if a tag is checked -->
        <div v-if="willCompActivities" class="ui form field">
          <textarea
            style="width: 100%; margin-top: 15px; margin-bottom: 15px"
            type="text"
            maxlength="1000"
            v-model="props.collabInput.bigTicketItemBookinNotes"
            placeholder="Ex. Meal at the Hungry Gator"
            @change="requestCollabInputSave"
          ></textarea>
        </div>

        <!-- We let them save the above field but this doesn't make sense to set until a creator has been chosen -->
        <div v-if="props.creatorInput && willCompActivities && !props.collabInput.bigTicketItemsAllBooked" class="lodging-checkbox-n-info" style="width: 190px">
          <SrpButton
            class="lodging-checkbox-n-info__checkbox-component"
            :size="screenSize === 'tablet' ? 'tiny' : 'small'"
            color="orange"
            @click="
              () => {
                props.collabInput.bigTicketItemsAllBooked = true;
                requestCollabInputSave();
              }
            "
            :style="{ maxWidth: screenSize === 'tablet' ? '185px' : 'unset' }"
          >
            Mark Activities Coordinated
          </SrpButton>

          <InfoIconWithTooltip class="lodging-checkbox-n-info__tooltip-wrap">Check this when activities have been coordinated for the creator.</InfoIconWithTooltip>
        </div>

        <div v-else-if="props.collabInput.bigTicketItemsAllBooked" style="display: flex; align-items: center">
          <div class="lodging-checkbox-n-info" style="margin-right: 10px">
            <IconEmbedded name="check_4-5" :size="14" style="margin-right: 4px" color="rgba(61, 133, 40, 1)" />
            Activities are coordinated
          </div>

          <LinkWithIcon
            isDottedUnderline
            style="position: relative; top: -1px"
            @click="
              () => {
                props.collabInput.bigTicketItemsAllBooked = false;
                requestCollabInputSave();
              }
            "
          >
            <span>undo</span>
          </LinkWithIcon>
        </div>
      </div>
      <!-- / Comped Activities Section -->

      <!-- Visit plan section -->
      <div v-else-if="visitStep.isVisitPlanSection">
        <!-- Plan status line -->
        <div v-if="!props.creatorInput?.visitPlanSentDate && !props.creatorInput?.visitPlanSignOffDate" class="plan-status-line" style="margin-bottom: 9px">
          <div class="plan-status-line__circle"></div>
          <div class="plan-status-line__text">Not yet received from the creator</div>
        </div>
        <!-- / Plan status line -->

        <!-- Plan status line -->
        <div v-else-if="props.creatorInput?.visitPlanSentDate && !props.creatorInput?.visitPlanSignOffDate" class="plan-status-line plan-status-line--orange" style="margin-bottom: 9px">
          <div class="plan-status-line__circle"></div>
          <div class="plan-status-line__text">Waiting to be reviewed</div>
        </div>
        <!-- / Plan status line -->

        <!-- Plan status line -->
        <div v-else-if="props.creatorInput?.visitPlanSignOffDate" class="plan-status-line plan-status-line--green" style="margin-bottom: 9px">
          <div class="plan-status-line__circle"></div>
          <div class="plan-status-line__text">Signed Off</div>
        </div>
        <!-- / Plan status line -->

        <template v-if="props.creatorInput?.visitPlanSentDate">
          <SrpButton v-if="props.creatorInput?.visitPlanSignOffDate" size="small" @click="isVisitPlanApprovalModalVisible = true"> View </SrpButton>
          <SrpButton v-else color="orange" size="small" @click="isVisitPlanApprovalModalVisible = true"> Review </SrpButton>
        </template>
      </div>
      <!-- / Visit plan section -->
    </VisitStepRow>

    <VisitPlanApprovalModal
      v-model:isVisible="isVisitPlanApprovalModalVisible"
      @connectWithTheCreator="
        () => {
          isVisitPlanApprovalModalVisible = false;
          emit('openChat');
        }
      "
      :planText="props.collabInput.useLocationsInVisitPlan ? collabLocationsStore.locationsTextRepresentation : props.creatorInput?.visitPlan"
      :signOffDate="props.creatorInput?.visitPlanSignOffDate"
      :communityId="props.communityId"
      :collabInputId="props.collabInput.id"
      :requestedLocationCount="props.collabInput?.requestedLocationCount"
      :requestedAdventureCount="props.collabInput?.requestedAdventureCount"
      @signOff="signOff"
      :creatorName="props.creatorName"
    />
  </ul>
</template>

<!-- prettier-ignore -->
<script lang="ts">export default { name: "VisitCheckList" };</script>

<script setup lang="ts">
import Logger from "@helpers/Logger";
import { ref, computed, onMounted, watch, inject, Ref } from "vue";
import { RouteHelper } from "@helpers/RouteHelper";

// Types
import { CollabChecklistRow } from "@contracts/collabChecklist";
import { CollabCreatorInput, CollabInput, CustomerCollabRemainingCounts } from "@contracts/collab";
import { ScreenSize } from "@contracts/screenSize";

// Components
import CollabCompTags from "@views/PaidCollab/CollabCompTags.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import InfoIconWithTooltip from "@components/ui/InfoIconWithTooltip.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import VisitPlanApprovalModal from "@views/PaidCollab/CollabSummary/VisitCheckList/VisitPlanApprovalModal.vue";
import VisitStepRow from "./VisitStepRow.vue";

// Stores
import { useCollabLocationsStore } from "@stores/collabLocations";
import { useToastsStore } from "@stores/toasts";
const collabLocationsStore = useCollabLocationsStore();
const toastsStore = useToastsStore();

const props = withDefaults(
  defineProps<{
    checklistRows: Array<CollabChecklistRow>;
    lodgingDetailsCollapsed: boolean;
    collabInput: CollabInput | null;
    creatorInput: CollabCreatorInput | null;
    collabRemainingCounts: CustomerCollabRemainingCounts | null;
    adventuresAreCreated: boolean;
    compDetailsCollapsed: boolean;
    isVisitPlanCollapsed: boolean;
    calculateChecklist?: Function;
    communityId: string;
    creatorName: string;
    isSavingCollabInput?: boolean;
  }>(),
  {
    checklistRows: () => [],
    lodgingDetailsCollapsed: false,
    collabInput: null,
    creatorInput: null,
    collabRemainingCounts: null,
    adventuresAreCreated: false,
    compDetailsCollapsed: false,
    calculateChecklist: () => {},
    communityId: "",
    creatorName: "",
    isSavingCollabInput: false,
  }
);

const emit = defineEmits<{
  (e: "update:lodgingDetailsCollapsed", value: boolean): void;
  (e: "update:compDetailsCollapsed", value: boolean): void;
  (e: "update:isVisitPlanCollapsed", value: boolean): void;
  (e: "save"): void;
  (e: "openChat"): void;
  (e: "requestCollabInputSave"): void;
}>();

const screenSize = inject("screenSize") as Ref<ScreenSize>;

// ============================================================================
const creatorCoveringHotel = computed<boolean>(() => {
  return props.collabInput.hotelNightsCovered === 0 && props.creatorInput != null;
});

const willCompActivities = computed<boolean>(() => {
  return props.collabInput?.compedActivitiesTags?.length > 0;
});

const customerNotCoveringLodgingText = computed<string>(() => {
  if (props.collabInput.hotelCoverageUsed === "Grandfathered") return "Can't arrange Lodging";
  else {
    return `Use Paid Lodging AddOn`;
  }
});

// Hotel Coverage ==============================================================
function nightsCoveredChanged() {
  Logger.info("StepLodging nightsCoveredChanged", props.collabInput.hotelCoverageUsed);

  // NOTE! Duplicated in StepLodging!
  if (props.collabInput.hotelCoverageUsed === "Grandfathered") {
    // Nothing to adjust in the old model
    return;
  }

  // Otherwise we update the the hotelCoverageUsed for tracking
  if (props.collabInput.hotelNightsCovered === 0) {
    props.collabInput.hotelCoverageUsed = "CustomerChosePaidAddOn";
  } else {
    props.collabInput.hotelCoverageUsed = "CustomerChoseToArrange";
  }
}

// SignOff ====================================================================

async function signOff() {
  // Reloading the creatorInput is problematic because we don't use stores. Therefore, it's easier to manually update the visitPlanSignOffDate field with a value that is almost identical to the one in the database
  // NOTE: Refactor creatorInput to utilize a centralized store and populate the 'visitPlanSignOffDate' field with the data returned from the endpoint to avoid property mutation.
  props.creatorInput.visitPlanSignOffDate = new Date().toISOString();
  props.calculateChecklist();
}

// Toggle Visit Plan modal ====================================================
const isVisitPlanApprovalModalVisible = ref(false);

watch(
  () => props.creatorInput,
  () => {
    checkForVisitPlanShow();
  }
);

function checkForVisitPlanShow() {
  if (RouteHelper.getHashParam("isReviewVisitPlanModalVisible") && props.creatorInput?.visitPlanSentDate && !props.creatorInput?.visitPlanSignOffDate) {
    console.info("Auto-opening the visit plan modal");
    isVisitPlanApprovalModalVisible.value = true;
  }
}

// Load collab locations data =================================================
onMounted(async () => {
  if (props.collabInput?.id && props.creatorInput?.creatorId && props.communityId) {
    await collabLocationsStore.loadCollabLocationsData(props.collabInput.id, props.creatorInput?.creatorId, props.communityId);
  }
});

watch(
  () => [props.creatorInput, props.collabInput, props.communityId],
  async () => {
    await collabLocationsStore.loadCollabLocationsData(props.collabInput.id, props.creatorInput?.creatorId, props.communityId);
  }
);

// Request collab input save ==================================================
function requestCollabInputSave() {
  emit("requestCollabInputSave");
  toastsStore.success({ message: "Changes saved", expiresInMs: 2000 });
}
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";

// Form container =============================================================
.form-container {
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .form-container {
    max-width: 175px;
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
}

// Plan status line ===========================================================
.plan-status-line {
  display: flex;
  align-items: center;
  color: #666666;
  font-weight: bold;

  &__circle {
    width: 7px;
    height: 7px;
    margin: -1px 5px 0 0;
    border-radius: 100px;
    background: #666666;
  }

  &--green {
    color: #3d8528;

    .plan-status-line__circle {
      background: #3d8528;
    }
  }
  &--orange {
    color: #bb9227;

    .plan-status-line__circle {
      background: #bb9227;
    }
  }

  &__text {
  }
}

// Info icon ==================================================================
.info-icon {
  min-width: 23px;
  min-height: 23px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  position: relative;
  cursor: pointer;

  &::before {
    content: "";
    width: 100%;
    height: 100%;
    border-radius: 100px;
    position: absolute;
    inset: 0 auto auto 0;
    background: rgba(0, 0, 0, 0.1);
    transition:
      width 0.05s ease-in-out,
      height 0.05s ease-in-out,
      top 0.05s ease-in-out,
      left 0.05s ease-in-out;
  }

  &::after {
    content: "";
    width: calc(100% + 22px);
    height: calc(100% + 22px);
    position: absolute;
    inset: 50% auto auto 50%;
    transform: translate(-50%, -50%);
  }

  &--expanded {
    &::before {
      width: calc(100% + 6px);
      height: calc(100% + 6px);
      inset: -3px auto auto -3px;
    }
  }

  &__symbol {
  }
}

// Lodging checkbox & info ====================================================
.lodging-checkbox-n-info {
  display: inline-flex;
  align-items: center;
  position: relative;
  white-space: nowrap;

  &__checkbox-component {
    margin-right: 8px;
  }

  &__tooltip-wrap {
  }

  &__info-icon {
  }
}

// Visit checklist ============================================================
.visit-checklist {
  padding: 0;
  margin: 0;
  list-style: none;

  &__visit-step {
    width: calc(100% + 25px + 23px);
    margin-left: -25px;

    &--hidden {
      display: none;
    }
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .visit-checklist {
    &__visit-step {
      width: calc(100% + 25px + 22px);
    }
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .visit-checklist {
    &__visit-step {
      width: calc(100% + 15px + 13px);
      margin-left: -14px;
    }
  }
}
</style>
